import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Table } from 'primeng/table'
import { BehaviorSubject, Subscription, Observable, forkJoin, of } from 'rxjs'
import { debounceTime, distinctUntilChanged, switchMap, finalize } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import {
  AccountSubjectDto,
  AccountSubjectGroupDto,
  RoleDto,
  AuthenticatedSubjectDto,
  ApplicationDto,
} from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { InviteUsersModalComponent } from '../invite-users-modal/invite-users-modal.component'
import { EditUserModalComponent } from '../edit-user-modal/edit-user-modal.component'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { SidepanelService, SidepanelController } from '../../services/sidepanel.service'
import { DateFormattingService } from 'src/app/services/date-formatting.service'
import { computeSsoRolesAndGroups } from 'src/app/shared/sso-utils'

export interface UserManagementItem {
  name: string
  email: string
  lastActive: Date | null
  identityProviderName: string
  verified: boolean
  accountSubjectId: string
  subject: AccountSubjectDto
  roles: { name: string; id: string }[]
  groups: { name: string; id: string }[]
  ssoRoles: { name: string; id: string }[]
  ssoGroups: { name: string; id: string }[]
}

export interface UserRoleInfo {
  id: string
  name: string
  accessLevel: string
  isSystemRole?: boolean
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  host: { class: 'rbac-management-page' },
  standalone: false,
})
export class UserManagementComponent implements OnInit, OnDestroy, SidepanelController {
  // Make Math available to the template
  Math = Math

  users: UserManagementItem[] = null
  filteredUsers: UserManagementItem[] = []
  selectedUser: UserManagementItem | null = null
  selectedTableItem: UserManagementItem | null = null // For table selection binding
  showSidePanel = false
  deletingUser = false
  userToDelete: UserManagementItem | null = null
  refreshingUserIds: Set<string> = new Set()
  openingInviteModal = false

  filterText = ''
  isFilterApplied = false
  loadingFilterInput = false
  debounceSubject = new BehaviorSubject<string>('')

  // Role and group filtering
  selectedRoleFilters: string[] = []
  selectedGroupFilters: string[] = []
  selectedIdpFilters: string[] = []
  showRoleFilterDropdown = false

  // Collapsible sections state
  filterSections = {
    identityProviders: false,
    roles: false,
    groups: false,
  }

  @ViewChild('dt1') usersTable: Table
  @ViewChild('filterInput') filterInput!: ElementRef
  @ViewChild('filterDropdown') filterDropdown!: ElementRef
  @ViewChild('inviteUsersModal') inviteUsersModal: InviteUsersModalComponent
  @ViewChild('editUserModal') editUserModal: EditUserModalComponent
  @ViewChild('deleteUserDialog') deleteUserDialog: ModalDialogComponent
  @ViewChild('sidePanel') sidePanel: ElementRef

  private loadingUserDataSubscription: Subscription = null

  // User role data
  private userRolesCache: Map<string, UserRoleInfo[]> = new Map()
  private userGroupsCache: Map<string, any[]> = new Map()
  private allGroups: any[] = []

  // Authenticated subject for preventing self-deletion
  authenticatedSubject: AuthenticatedSubjectDto | null = null
  private allRoles: RoleDto[] = []
  private allApplications: ApplicationDto[] = []

  // Permission properties
  canManageUsers$: Observable<boolean>
  canViewUsers$: Observable<boolean>
  unauthorizedMessage$: Observable<string>
  // Store actual permission values for synchronous access
  private canManageUsersValue: boolean = false
  private canViewUsersValue: boolean = false

  constructor(
    private apiService: ApiService,
    private uiService: UiService,
    public accountService: AccountService,
    private router: Router,
    private route: ActivatedRoute,
    private accessLevelService: AccessLevelService,
    private sidepanelService: SidepanelService,
    private dateFormattingService: DateFormattingService
  ) {}

  ngOnInit() {
    // Initialize permission observables
    this.canManageUsers$ = this.accessLevelService.hasAccountPermission(AccountPermission.MANAGE_RBAC)
    this.canViewUsers$ = this.accessLevelService.hasAccountPermission(AccountPermission.READ_ACCOUNT_SUBJECT)
    this.unauthorizedMessage$ = of(
      this.accessLevelService.getUnauthorizedMessage(AccountPermission.READ_ACCOUNT_SUBJECT)
    )

    // Subscribe to permission values for synchronous access
    this.canManageUsers$.subscribe((value) => {
      this.canManageUsersValue = value
    })
    this.canViewUsers$.subscribe((value) => {
      this.canViewUsersValue = value
      // Redirect to account root if user doesn't have view permission
      if (!value) {
        this.router.navigate([`/${this.accountService.accountId}`])
      }
    })

    this.checkForUserSelection()
    this.loadAllDataInParallel()
    this.setupDebounceFilter()
    this.setupClickOutsideListener()

    // Register with sidepanel service
    this.sidepanelService.register(this, this)
  }

  private loadAllDataInParallel() {
    this.loadingUserDataSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((_accountId) => {
          // Load all initial data in parallel
          return forkJoin({
            users: this.apiService.listAccountSubjects(this.accountService.accountId),
            roles: this.apiService.listRoles(this.accountService.accountId),
            groups: this.apiService.listAccountSubjectGroups(this.accountService.accountId),
            applications: this.apiService.listApplications(this.accountService.accountId),
            authenticatedSubject: this.apiService.getAuthenticatedSubject(),
          })
        }),
        finalize(() => {
          this.loadingUserDataSubscription = null
        })
      )
      .subscribe(
        (data) => {
          // Store authenticated subject
          this.authenticatedSubject = data.authenticatedSubject

          // Store roles, groups, and applications
          this.allRoles = data.roles
          this.allGroups = data.groups
          this.allApplications = data.applications

          // Process users
          this.users = data.users
            .map((subject) => {
              const idpName = subject.identityProviderId
                ? this.accountService.getIdentityProvider(subject.identityProviderId)?.name || 'Arpio'
                : 'Arpio'
              return {
                name: subject.name || subject.email || '',
                email: subject.email || '',
                lastActive: subject.lastLoginAt ? new Date(subject.lastLoginAt) : null,
                identityProviderName: idpName,
                verified: subject.verified || false,
                accountSubjectId: subject.accountSubjectId,
                subject: subject,
                roles: [],
                groups: [],
                ssoRoles: [],
                ssoGroups: [],
              }
            })
            .sort((a, b) => {
              // Current user should always be first
              const aIsCurrent =
                this.authenticatedSubject && a.subject?.subjectId === this.authenticatedSubject.subjectId
              const bIsCurrent =
                this.authenticatedSubject && b.subject?.subjectId === this.authenticatedSubject.subjectId

              if (aIsCurrent && !bIsCurrent) return -1
              if (bIsCurrent && !aIsCurrent) return 1

              // For all other users, sort alphabetically by name
              return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            })
          this.filteredUsers = [...this.users]

          // Clear caches
          this.userRolesCache.clear()
          this.userGroupsCache.clear()
          this.refreshingUserIds.clear()

          // Now load roles and groups for each user
          this.loadUserRolesAndGroups()

          // Re-apply filters to maintain filter state after reload
          this.applyFilters()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  ngOnDestroy() {
    if (subscriptionActive(this.loadingUserDataSubscription)) {
      this.loadingUserDataSubscription.unsubscribe()
      this.loadingUserDataSubscription = null
    }
    this.removeClickOutsideListener()

    // Unregister from sidepanel service
    this.sidepanelService.unregister(this)
  }

  private setupClickOutsideListener() {
    document.addEventListener('click', this.onFilterDropdownDocumentClick.bind(this))
  }

  private removeClickOutsideListener() {
    document.removeEventListener('click', this.onFilterDropdownDocumentClick.bind(this))
  }

  private onFilterDropdownDocumentClick(event: Event) {
    if (this.showRoleFilterDropdown && this.filterDropdown) {
      const target = event.target as HTMLElement
      const dropdownElement = this.filterDropdown.nativeElement

      if (!dropdownElement.contains(target)) {
        this.showRoleFilterDropdown = false
      }
    }
  }

  private checkForUserSelection() {
    this.route.queryParams.subscribe((params) => {
      const userToSelect = params['selectUser']
      if (userToSelect) {
        this.selectUserWhenLoaded(userToSelect)
      }
    })
  }

  private selectUserWhenLoaded(userIdentifier: string) {
    if (this.users) {
      // Try to find by account subject ID
      let user = this.users.find((u) => u.accountSubjectId === userIdentifier)

      if (user) {
        this.selectedUser = user
        this.selectedTableItem = user
        this.openSidePanel()
      }
    } else {
      // Retry after data loads
      setTimeout(() => this.selectUserWhenLoaded(userIdentifier), 100)
    }
  }

  private setupDebounceFilter() {
    this.debounceSubject.pipe(debounceTime(600), distinctUntilChanged()).subscribe((filterText: string) => {
      this.applyFilter(filterText)
      this.loadingFilterInput = false
    })
  }

  loadUserData() {
    // Reuse the parallel loading method for consistency
    this.loadAllDataInParallel()
  }

  // Helper method to identify default account roles
  public isDefaultAccountRole(role: any): boolean {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  private loadUserRolesAndGroups() {
    if (!this.users || !this.allRoles || !this.allGroups) return

    for (const user of this.users) {
      // Load user roles using the same logic as getUserRoles()
      const allRoleIds = new Set<string>()

      // Add directly assigned roles
      if (user.subject.roles && user.subject.roles.length > 0) {
        user.subject.roles.forEach((roleId) => allRoleIds.add(roleId))
      }

      // Add roles from groups
      const userGroups = this.allGroups.filter(
        (group) => group.accountSubjects && group.accountSubjects.includes(user.accountSubjectId)
      )

      userGroups.forEach((group) => {
        if (group.roles && group.roles.length > 0) {
          group.roles.forEach((roleId: string) => allRoleIds.add(roleId))
        }
      })

      // Convert role IDs to role info
      user.roles = Array.from(allRoleIds)
        .map((roleId) => {
          const roleDto = this.allRoles.find((role) => role.roleId === roleId)
          return roleDto
            ? {
                name: roleDto.name || 'Unknown Role',
                id: roleDto.roleId || '',
              }
            : null
        })
        .filter((role) => role !== null)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())) as { name: string; id: string }[]

      // Set user groups
      user.groups = userGroups
        .map((group) => ({
          name: group.name || 'Unknown Group',
          id: group.accountSubjectGroupId || '',
        }))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

      // Compute SSO groups and roles using shared utility
      const ssoData = computeSsoRolesAndGroups(
        user.subject.lastSsoGroups,
        user.subject.lastSsoRoles,
        this.allGroups,
        this.allRoles
      )
      user.ssoGroups = ssoData.ssoGroups
      user.ssoRoles = ssoData.ssoRoles
    }

    // Re-sort users with logged-in user first, then alphabetically by name
    this.users.sort((a, b) => {
      const aIsLoggedIn = this.isLoggedInUser(a)
      const bIsLoggedIn = this.isLoggedInUser(b)

      // Logged-in user always comes first
      if (aIsLoggedIn && !bIsLoggedIn) return -1
      if (!aIsLoggedIn && bIsLoggedIn) return 1

      // For all other users, sort alphabetically by name
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    })

    this.filteredUsers = [...this.users]
  }

  onRowSelect(user: UserManagementItem) {
    this.selectedUser = user
    this.selectedTableItem = user
    // Opening the sidepanel will be handled by the selectedUser setter or watcher
    this.openSidePanel()
  }

  private openSidePanel() {
    if (this.selectedUser) {
      this.showSidePanel = true
      // Any additional data loading that should happen when sidepanel opens can go here
      // Currently no additional loading is needed since getUserRoles() and getUserGroups()
      // use already loaded data
    }
  }

  closeSidePanel() {
    this.showSidePanel = false
    this.selectedUser = null
    this.selectedTableItem = null // Clear the table selection binding
  }

  filterGlobal(event: any) {
    this.loadingFilterInput = true
    const filterText = event.target.value
    this.debounceSubject.next(filterText)
  }

  applyFilter(filterText: string) {
    if (!this.usersTable || typeof filterText !== 'string') return

    const filterValue = filterText.toLowerCase().trim()

    if (!filterValue) {
      this.usersTable.clear()
      this.filterText = filterValue
      this.isFilterApplied = false
      return
    }

    this.usersTable.filterGlobal(filterValue, 'contains')
    this.filterText = filterValue
    this.isFilterApplied = true
  }

  clearInput() {
    this.filterText = ''
    this.filterInput.nativeElement.value = ''
    this.isFilterApplied = false
    this.usersTable.clear()
  }

  // Role filtering methods
  toggleRoleFilterDropdown() {
    this.showRoleFilterDropdown = !this.showRoleFilterDropdown
  }

  onRoleFilterChange(roleName: string, checked: boolean) {
    if (checked) {
      this.selectedRoleFilters.push(roleName)
    } else {
      this.selectedRoleFilters = this.selectedRoleFilters.filter((r) => r !== roleName)
    }
    this.applyFilters()
  }

  onGroupFilterChange(groupName: string, checked: boolean) {
    if (checked) {
      this.selectedGroupFilters.push(groupName)
    } else {
      this.selectedGroupFilters = this.selectedGroupFilters.filter((g) => g !== groupName)
    }
    this.applyFilters()
  }

  onIdpFilterChange(idpName: string, checked: boolean) {
    if (checked) {
      this.selectedIdpFilters.push(idpName)
    } else {
      this.selectedIdpFilters = this.selectedIdpFilters.filter((i) => i !== idpName)
    }
    this.applyFilters()
  }

  toggleFilterSection(section: keyof typeof this.filterSections) {
    this.filterSections[section] = !this.filterSections[section]
  }

  applyFilters() {
    let filtered = [...this.users]

    // Apply role filters (include both direct roles and SSO roles)
    if (this.selectedRoleFilters.length > 0) {
      filtered = filtered.filter((user) => {
        const hasDirectRole = user.roles.some((role) => this.selectedRoleFilters.includes(role.name))
        const hasSsoRole = user.ssoRoles.some((role) => this.selectedRoleFilters.includes(role.name))
        return hasDirectRole || hasSsoRole
      })
    }

    // Apply group filters (include both direct groups and SSO groups)
    if (this.selectedGroupFilters.length > 0) {
      filtered = filtered.filter((user) => {
        const hasDirectGroup = user.groups.some((group) => this.selectedGroupFilters.includes(group.name))
        const hasSsoGroup = user.ssoGroups.some((group) => this.selectedGroupFilters.includes(group.name))
        return hasDirectGroup || hasSsoGroup
      })
    }

    // Apply identity provider filters
    if (this.selectedIdpFilters.length > 0) {
      filtered = filtered.filter((user) => {
        const idpName = this.getIdentityProviderName(user)
        return this.selectedIdpFilters.includes(idpName)
      })
    }

    this.filteredUsers = filtered
  }

  clearRoleFilters() {
    this.selectedRoleFilters = []
    this.selectedGroupFilters = []
    this.selectedIdpFilters = []
    this.applyFilters()
    this.showRoleFilterDropdown = false
  }

  getAvailableRoles(): string[] {
    if (!this.allRoles) return []
    return this.allRoles
      .map((role) => role.name)
      .filter((name) => name)
      .sort()
  }

  getAvailableGroups(): string[] {
    if (!this.allGroups) return []
    return this.allGroups
      .map((group) => group.name)
      .filter((name) => name)
      .sort()
  }

  getAvailableIdentityProviders(): string[] {
    if (!this.users) return []
    const idpNames = new Set<string>()

    this.users.forEach((user) => {
      const idpName = this.getIdentityProviderName(user)
      if (idpName) {
        idpNames.add(idpName)
      }
    })

    return Array.from(idpNames).sort()
  }

  openInviteUsersModal() {
    this.openingInviteModal = true
    this.inviteUsersModal
      .open()
      .then(() => {
        // Modal opened successfully
        this.openingInviteModal = false
      })
      .catch((error) => {
        console.error('Failed to open invite users modal:', error)
        this.openingInviteModal = false
      })
  }

  openEditUserModal() {
    if (this.selectedUser) {
      this.editUserModal.open(this.selectedUser)
    }
  }

  isNativeArpioUser(user: UserManagementItem): boolean {
    const idp = user?.subject?.identityProviderId
      ? this.accountService.getIdentityProvider(user.subject.identityProviderId)
      : null
    return !user?.subject?.identityProviderId || idp?.type?.toLowerCase() === 'native'
  }

  getIdentityProviderName(user: UserManagementItem): string {
    if (!user?.subject?.identityProviderId) return 'Arpio'
    const idp = this.accountService.getIdentityProvider(user.subject.identityProviderId)
    return idp?.name || 'Arpio'
  }

  // Real data methods - get user's roles from direct assignment and groups
  getUserRoles(): UserRoleInfo[] {
    if (!this.selectedUser) {
      return []
    }

    // Check cache first
    const cacheKey = this.selectedUser.accountSubjectId
    if (this.userRolesCache.has(cacheKey)) {
      return this.userRolesCache.get(cacheKey) || []
    }

    // Collect role IDs from both direct assignment and groups
    const allRoleIds = new Set<string>()

    // Add directly assigned roles
    if (this.selectedUser.subject.roles && this.selectedUser.subject.roles.length > 0) {
      this.selectedUser.subject.roles.forEach((roleId) => allRoleIds.add(roleId))
    }

    // Add roles from groups
    const userGroups = this.getUserGroups()
    userGroups.forEach((group) => {
      if (group.roles && group.roles.length > 0) {
        group.roles.forEach((roleId: string) => allRoleIds.add(roleId))
      }
    })

    const roleIdArray = Array.from(allRoleIds)
    if (roleIdArray.length === 0) {
      return []
    }

    // Use the already loaded allRoles instead of making individual API calls
    const userRoleInfos = roleIdArray
      .map((roleId) => {
        const roleDto = this.allRoles.find((role) => role.roleId === roleId)
        return roleDto ? this.convertRoleToUserRoleInfo(roleDto) : null
      })
      .filter((role) => role !== null) as UserRoleInfo[]

    // Sort roles with default account roles first
    const sortedRoles = this.sortRolesWithAccountRolesFirst(userRoleInfos)

    // Cache the result
    this.userRolesCache.set(cacheKey, sortedRoles)

    return sortedRoles
  }

  private convertRoleToUserRoleInfo(roleDto: RoleDto): UserRoleInfo {
    // Default access level
    const accessLevel = 'app_read'

    return {
      id: roleDto.roleId || '',
      name: roleDto.name || '',
      accessLevel: accessLevel,
      isSystemRole: roleDto.isSystemRole || false,
    }
  }

  private sortRolesWithAccountRolesFirst(roles: UserRoleInfo[]): UserRoleInfo[] {
    return roles.sort((a, b) => {
      // System roles always first
      if (a.isSystemRole && !b.isSystemRole) return -1
      if (!a.isSystemRole && b.isSystemRole) return 1

      // All other roles alphabetically
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    })
  }

  getUserGroups() {
    if (!this.selectedUser) return []

    // Find groups that contain this user
    const userGroups = this.allGroups.filter((group) => {
      // Check if this user is in the group's accountSubjects array
      return group.accountSubjects && group.accountSubjects.includes(this.selectedUser.accountSubjectId)
    })

    return userGroups
  }

  navigateToRole(roleName: string) {
    // Navigate to role management page with query parameters to select the role
    this.router.navigate([`/${this.accountService.accountId}/settings/role-management`], {
      queryParams: { selectRole: roleName },
    })
  }

  navigateToGroup(groupName: string) {
    // Navigate to group management page with query parameters to select the group
    this.router.navigate([`/${this.accountService.accountId}/settings/group-management`], {
      queryParams: { selectGroup: groupName },
    })
  }

  userTrackBy(_index: number, user: UserManagementItem) {
    return user.accountSubjectId
  }

  getLastActiveDisplay(lastActive: Date | null): string {
    return this.dateFormattingService.getRelativeTimeDisplay(lastActive, 'Never')
  }

  getLastActiveTooltip(lastActive: Date | null): string | null {
    return this.dateFormattingService.getRelativeTimeTooltip(lastActive)
  }

  getLastActiveSidePanelDisplay(lastActive: Date | null): string {
    if (!lastActive) return 'Never'

    const relativeDisplay = this.dateFormattingService.getRelativeTimeDisplay(lastActive, 'Never')
    const tooltip = this.dateFormattingService.getRelativeTimeTooltip(lastActive)

    // If there's a tooltip, combine the relative display with the full date/time
    if (tooltip) {
      return `${relativeDisplay}, ${tooltip}`
    }

    // For absolute dates, just return the relative display
    return relativeDisplay
  }

  get globalFilterFields() {
    return ['name', 'email', 'identityProviderName', 'roles', 'groups']
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  get rbacFullEnabled(): boolean {
    return this.accountService.rbacFullEnabled
  }

  deleteUser() {
    if (!this.selectedUser || !this.canDeleteUser()) return

    if (this.selectedUser.subject?.subjectId === this.authenticatedSubject?.subjectId) {
      // This case should never happen, but just in case
      console.error('Cannot delete currently logged in user')
      return
    }

    // Store the user to delete
    this.userToDelete = this.selectedUser
    // Show the confirmation modal
    this.deleteUserDialog.open()
  }

  cancelDeleteUser() {
    this.deleteUserDialog.close()
  }

  confirmDeleteUser() {
    if (!this.userToDelete) return

    this.deletingUser = true
    this.deleteUserDialog.error = null // Clear any previous errors

    this.apiService.deleteAccountSubject(this.accountService.accountId, this.userToDelete.accountSubjectId).subscribe(
      () => {
        this.deleteUserDialog.close()
        this.closeSidePanel()
        this.loadUserData() // Refresh the list
        this.deletingUser = false
        this.userToDelete = null // Clear the stored user
      },
      (error) => {
        this.deleteUserDialog.error =
          'Unable to delete this user. Please try again later. If the issue persists, please contact support.'
        this.deletingUser = false
      }
    )
  }

  resendInviteEmail() {
    if (!this.selectedUser) return

    this.apiService.resendInvite(this.accountService.accountId, this.selectedUser.accountSubjectId).subscribe(
      () => {
        // Success - could show a success message if needed
      },
      (error) => {
        console.error('Error resending invite:', error)
        this.uiService.announceApiError(error, 'Failed to resend invitation')
      }
    )
  }

  resendVerificationEmail() {
    if (!this.selectedUser) return

    this.apiService
      .resendVerificationEmail(this.accountService.accountId, this.selectedUser.accountSubjectId)
      .subscribe(
        () => {
          // Success - could show a success message if needed
        },
        (error) => {
          console.error('Error resending verification email:', error)
          this.uiService.announceApiError(error, 'Failed to resend verification email')
        }
      )
  }

  canResendInviteEmail(): boolean {
    if (!this.selectedUser || !this.isNativeArpioUser(this.selectedUser)) {
      return false
    }

    // Check if user has permission to invite users
    if (!this.canInviteUsers()) {
      return false
    }

    const hasNoSubjectId = !this.selectedUser.subject?.subjectId

    // Show invite button when user hasn't accepted invite yet (no subjectId)
    return hasNoSubjectId
  }

  // Check if current user has permission to invite users
  canInviteUsers(): boolean {
    return this.canManageUsersValue
  }

  canResendVerificationEmail(): boolean {
    if (!this.selectedUser || !this.isNativeArpioUser(this.selectedUser)) {
      return false
    }

    // Check if user has permission to manage users (needed for verification resend)
    if (!this.canInviteUsers()) {
      return false
    }

    const hasSubjectId = !!this.selectedUser.subject?.subjectId
    const isNotVerified = !this.selectedUser.verified
    const result = isNotVerified && hasSubjectId

    // Show verification button when user has accepted invite (has subjectId) but isn't verified
    return result
  }

  canDeleteUser(): boolean {
    if (!this.selectedUser || !this.authenticatedSubject) {
      return false
    }

    // Check if user has permission to manage users (admin only)
    if (!this.canInviteUsers()) {
      return false
    }

    // Don't allow deletion of the currently authenticated user (root user protection)
    return !this.isLoggedInUser(this.selectedUser)
  }

  // Check if a user is the currently logged-in user
  isLoggedInUser(user: UserManagementItem): boolean {
    if (!this.authenticatedSubject || !user?.subject?.subjectId) return false
    return user.subject.subjectId === this.authenticatedSubject.subjectId
  }

  // Modal event handlers
  onUsersInvited() {
    this.closeSidePanel()
    this.loadUserData()
  }

  onUserUpdated(user: UserManagementItem) {
    // Add the user to the refreshing set to show spinner
    this.refreshingUserIds.add(user.accountSubjectId)
    this.closeSidePanel()
    this.loadUserData()
  }

  isUserRefreshing(user: UserManagementItem): boolean {
    return this.refreshingUserIds.has(user.accountSubjectId)
  }

  // Tooltip methods for delay and hover functionality
  private tooltipHideTimer: any

  keepTooltipVisible() {
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer)
      this.tooltipHideTimer = null
    }
  }

  hideTooltipDelayed() {
    this.tooltipHideTimer = setTimeout(() => {
      // Logic to hide tooltip - this depends on the tooltip implementation
      // For now, we'll let the PrimeNG tooltip handle it naturally
    }, 200)
  }

  // Get tooltip text for additional roles (roles beyond the first 2 displayed)
  getAdditionalRolesTooltip(user: UserManagementItem): string {
    const totalRoles = user.roles.length + user.ssoRoles.length
    if (totalRoles <= 2) return ''

    // Combine manual and SSO roles, showing only those beyond the first 2
    const allRoles = [...user.roles, ...user.ssoRoles]
    const additionalRoles = allRoles.slice(2)

    const badgeHtml = additionalRoles
      .map((role, index) => {
        const isSsoRole =
          index >= user.roles.length - 2 && user.roles.length < 2 ? true : index >= user.roles.slice(2).length
        const iconHtml = isSsoRole
          ? '<i class="fa-utility-duo fa-semibold fa-id-card" style="margin-right: 4px;"></i>'
          : ''
        return `<div class="tooltip-badge">${iconHtml}${role.name}</div>`
      })
      .join('')
    return `<div class="tooltip-badges-container">${badgeHtml}</div>`
  }

  // Get tooltip text for additional groups (groups beyond the first 2 displayed)
  getAdditionalGroupsTooltip(user: UserManagementItem): string {
    const totalGroups = user.groups.length + user.ssoGroups.length
    if (totalGroups <= 2) return ''

    // Combine manual and SSO groups, showing only those beyond the first 2
    const allGroups = [...user.groups, ...user.ssoGroups]
    const additionalGroups = allGroups.slice(2)

    const badgeHtml = additionalGroups
      .map((group, index) => {
        const isSsoGroup =
          index >= user.groups.length - 2 && user.groups.length < 2 ? true : index >= user.groups.slice(2).length
        const iconHtml = isSsoGroup
          ? '<i class="fa-utility-duo fa-semibold fa-id-card" style="margin-right: 4px;"></i>'
          : ''
        return `<div class="tooltip-badge">${iconHtml}${group.name}</div>`
      })
      .join('')
    return `<div class="tooltip-badges-container">${badgeHtml}</div>`
  }

  // Count the number of left-hand icons for a user (excluding spinner)
  getLeftIconCount(user: UserManagementItem): number {
    let count = 0

    // Count warning icon (unverified email)
    if (!user.verified) {
      count++
    }

    // Count external user icon (non-native Arpio user)
    if (!this.isNativeArpioUser(user)) {
      count++
    }

    // Note: We don't count the spinner icon since it's temporary and conditional on refresh state

    return count
  }
}
